import clsx from "clsx";
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useDebounce } from "../../hooks/useDebounce";
import { ExerciseAsset } from "../exercise-library/types";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { keepPreviousData } from "@tanstack/react-query";
import ExerciseAutocompleteOption from "./ExerciseAutocompleteOption";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { EXERCISE_LIBRARY_ASSET_LIST_QUERY_KEY } from "./ExerciseLibraryExercisesList";
import ExerciseAssetsService from "../../services/ExerciseAssetsService";
import ListboxExerciseWithFooterComponent from "../workout/ListboxExerciseFooterComponent";
import { ExerciseAssetType } from "@growth-machine-llc/stridist-api-client";
import { AddNewItemButton } from "../workout/AddNewItemButton";
import { UserRole } from "../../constants";
import { User } from "lucide-react";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    margin: 0,
  },

  popper: {
    zIndex: "99999 !important" as any,
    borderRadius: 12,
    boxShadow: theme.shadows[5],
  },
  listbox: {
    boxShadow: "none",
  },
  noOptions: {
    padding: 0,
  },
}));

export interface ExerciseAutocompleteProps
  extends Omit<
    AutocompleteProps<ExerciseAsset, true, false, any, any>,
    "options" | "onChange" | "onInputChange" | "renderInput"
  > {
  pageSize?: number;
  onChange?: (value: ExerciseAsset) => void;
  onCloseAutocomplete?: (options: ExerciseAsset[]) => void;
  onInputChange?: (
    e: any,
    value: string,
    options: {
      assetId: number;
      userId: number;
      exercise: any;
    }[],
  ) => void;
  renderCustomInput?: (
    params: AutocompleteRenderInputParams,
    isLoading: boolean,
  ) => React.ReactNode;
  onFetchingExercise?: (value: boolean) => void;
  onInitialExercise?: (exercise: any) => void;
  onDisableSave?: (value: boolean) => void;
  onCreateNew?: () => void;
  onOpenChange?: (open: boolean) => void;
  firstOpenFromSpreadsheet?: boolean;
  setFirstOpenFromSpreadsheet?: (value: boolean) => void;
  disableAddNewExerciseOnMatching?: boolean;
  open?: boolean;
  exerciseType: ExerciseAssetType;
}

export const getExerciseAssetLabel = (option: string | ExerciseAsset) => {
  if (typeof option === "string") {
    return option;
  }
  const {
    exercise: { title },
  } = option;

  return title;
};

const EXERCISE_AUTOCOMPLETE_PAGE_SIZE = 20;

export function ExerciseAutocomplete(props: ExerciseAutocompleteProps) {
  const {
    className,
    pageSize = 50,
    inputValue,
    onChange,
    onInputChange,
    onFetchingExercise,
    onInitialExercise,
    onCloseAutocomplete,
    onDisableSave,
    disabled: selectedOption,
    classes,
    onCreateNew,
    autoFocus,
    firstOpenFromSpreadsheet,
    setFirstOpenFromSpreadsheet,
    disableAddNewExerciseOnMatching = false,
    open: controlledOpen,
    exerciseType,
    ...other
  } = props;

  const [isAutocompleteClosed, setAutocompleteClosed] = useState(false);
  const delayedQuery = useDebounce(inputValue, 250);
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    if (firstOpenFromSpreadsheet) {
      const timeout = setTimeout(() => {
        setOpen(true);
        setFirstOpenFromSpreadsheet && setFirstOpenFromSpreadsheet(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
    setOpen(true);
  }, [firstOpenFromSpreadsheet, setFirstOpenFromSpreadsheet]);

  const [disableAddNewExercise, setDisableAddNewExercise] =
    useState<boolean>(false);

  const { id: userId, role: userRole, coach } = useCurrentUser();

  const coachAssetId = userRole === UserRole.COACH ? userId : coach?.id;

  const { assetSuggestionsEnabled } = useCurrentWorkspace();

  const {
    data: customAssetsData,
    isFetching,
    hasNextPage,
    isLoading,
    fetchNextPage,
  } = useInfiniteScrollQuery({
    queryKey: [EXERCISE_LIBRARY_ASSET_LIST_QUERY_KEY, { query: delayedQuery }],
    queryFn: ({ pageParam = 1 }) =>
      ExerciseAssetsService.getExerciseAssets(
        pageParam as number,
        EXERCISE_AUTOCOMPLETE_PAGE_SIZE,
        inputValue === "" ? undefined : inputValue.trim(),
        null,
        null,
        null,
        null,
        exerciseType,
      ),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const exerciseAssets =
    customAssetsData?.pages.flatMap((page) => page.items) ?? [];

  const options = React.useMemo(
    () =>
      exerciseAssets?.map((a) => ({
        assetId: a.id,
        userId: a.coachId,
        exercise: JSON.parse(a.content),
      })),
    [exerciseAssets],
  );

  const hasMatchingOption = options.some(
    (o) =>
      o.exercise?.title?.trim().toLowerCase() ===
      inputValue.trim().toLowerCase(),
  );

  const isClient = userRole === UserRole.CLIENT;
  const suggestionsDisabled = !assetSuggestionsEnabled;
  const inputIsEmpty = inputValue.trim().length === 0;
  const hasOtherOptions =
    options.length > 0 && (hasNextPage || hasMatchingOption);

  // For coaches both creating and selecting exercises are available
  // For clients creating new exercises is allowed only when all next conditions are met
  // 1. Suggestions are enabled by a coach
  // 2. Input is not empty
  // 3. There are no other options available (to avoid duplicates)
  const onlySelectAllowed =
    isClient && (suggestionsDisabled || inputIsEmpty || hasOtherOptions);

  useEffect(() => {
    if (onInitialExercise) {
      const matchedExercise = options.find(
        (o) => o.exercise.title.trim() === inputValue.trim(),
      );
      if (matchedExercise) {
        onInitialExercise(matchedExercise);
      }
    }
  }, [isLoading]);

  useEffect(() => {
    const matchedExercise = options.find(
      (o) => o.exercise.title.trim() === inputValue.trim(),
    );

    if (matchedExercise && disableAddNewExerciseOnMatching) {
      setDisableAddNewExercise(true);
    } else {
      setDisableAddNewExercise(false);
    }

    if (onDisableSave) {
      onDisableSave(options.length > 0 && !selectedOption && !!matchedExercise);
    }
  }, [options, inputValue, disableAddNewExerciseOnMatching]);
  const s = useStyles();

  const handleChange = React.useCallback(
    (_, value: ExerciseAsset) => {
      if (onChange) {
        onChange(value);
        setAutocompleteClosed(false);
        setOpen(false);
        if (props.onOpenChange) {
          props.onOpenChange(false);
        }
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (!isFetching && isAutocompleteClosed) {
      //Implement attaching matched exercise to the input
      if (onCloseAutocomplete) {
        onCloseAutocomplete(options);
      }
    }
  }, [isFetching, isAutocompleteClosed]);

  useEffect(() => {
    if (onFetchingExercise) {
      onFetchingExercise(isFetching);
    }
  }, [isFetching, onFetchingExercise]);

  const renderListbox = React.useCallback(
    (props) => (
      <ListboxExerciseWithFooterComponent
        {...props}
        onlySelectAllowed={onlySelectAllowed}
        onCreateNew={onCreateNew}
        disableAddNewExercise={disableAddNewExercise}
      />
    ),
    [onlySelectAllowed, onCreateNew, disableAddNewExercise],
  );

  return (
    <Autocomplete
      classes={{
        root: clsx(s.root, className),
        paper: s.paper,
        popper: s.popper,
        listbox: s.listbox,
        noOptions: s.noOptions,
        ...classes,
      }}
      open={open || controlledOpen}
      onOpen={() => {
        setOpen(true);
        setAutocompleteClosed(false);
        if (props.onOpenChange) {
          props.onOpenChange(true);
        }
      }}
      onFocus={() => {
        if (autoFocus) {
          setOpen(true);
          setAutocompleteClosed(false);
        }
      }}
      forcePopupIcon={false}
      filterOptions={(options) => options}
      inputValue={inputValue}
      clearOnBlur={false}
      options={options}
      disabled={selectedOption}
      renderOption={(props, option) => {
        const canEdit = option.userId === coachAssetId;
        return (
          <ExerciseAutocompleteOption
            {...props}
            key={option.assetId}
            option={option}
            classes={{ option: clsx(classes?.option) }}
            canEdit={canEdit}
          />
        );
      }}
      noOptionsText={
        !onlySelectAllowed && (
          <AddNewItemButton
            handleClick={onCreateNew}
            disabled={disableAddNewExercise}
            text="+ Add new exercise"
          />
        )
      }
      getOptionLabel={getExerciseAssetLabel}
      onChange={handleChange as any}
      onInputChange={(_, value) => onInputChange(_, value, options)}
      onClose={(_, reason) => {
        if (onCloseAutocomplete && (reason === "blur" || reason === "escape")) {
          onCloseAutocomplete(options);
          setAutocompleteClosed(true);
          setOpen(false);
          if (props.onOpenChange) {
            props.onOpenChange(false);
          }
        }
      }}
      disableClearable={false}
      slots={{
        listbox: renderListbox,
      }}
      renderInput={(params) => props.renderCustomInput(params, isFetching)}
      {...other}
      slotProps={{
        listbox: {
          onScroll: (event: React.SyntheticEvent) => {
            const listboxNode = event.currentTarget;
            if (
              Math.round(listboxNode.scrollTop) + listboxNode.clientHeight >
              listboxNode.scrollHeight - 100
            ) {
              if (!isFetching && hasNextPage) fetchNextPage();
            }
          },
        },
      }}
    />
  );
}
